import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

interface ContentSource {
  id: string;
  url: string;
  name: string;
  is_active: boolean;
  scraping_config: any;
  created_at: string;
  updated_at: string;
}

interface SourceFormData {
  url: string;
  name: string;
  is_active: boolean;
  crawl_depth: number;
  max_pages: number;
}

const SourceManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingSource, setEditingSource] = useState<ContentSource | null>(null);
  const [deletingSource, setDeletingSource] = useState<ContentSource | null>(null);
  const [formData, setFormData] = useState<SourceFormData>({
    url: '',
    name: '',
    is_active: true,
    crawl_depth: 0,
    max_pages: 10,
  });
  const [formErrors, setFormErrors] = useState<{ url?: string; name?: string }>({});
  const [error, setError] = useState<string | null>(null);

  // Fetch sources
  const { data: sourcesData, isLoading } = useQuery({
    queryKey: ['sources'],
    queryFn: async () => {
      const response = await api.get('/api/admin/sources');
      return response.data;
    },
  });

  // Create source mutation
  const createMutation = useMutation({
    mutationFn: async (data: SourceFormData) => {
      const response = await api.post('/api/admin/sources', null, {
        params: {
          url: data.url,
          name: data.name,
          is_active: data.is_active,
          crawl_depth: data.crawl_depth,
          max_pages: data.max_pages,
        },
      });
      return response.data;
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sources'] });
        handleCloseDialog();
        setError(null);
      } else {
        setError(data.error?.message || 'Failed to create source');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create source');
    },
  });

  // Update source mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SourceFormData }) => {
      const response = await api.put(`/api/admin/sources/${id}`, null, {
        params: {
          url: data.url,
          name: data.name,
          is_active: data.is_active,
          crawl_depth: data.crawl_depth,
          max_pages: data.max_pages,
        },
      });
      return response.data;
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sources'] });
        handleCloseDialog();
        setError(null);
      } else {
        setError(data.error?.message || 'Failed to update source');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update source');
    },
  });

  // Delete source mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/api/admin/sources/${id}`);
      return response.data;
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['sources'] });
        handleCloseDeleteDialog();
        setError(null);
      } else {
        setError(data.error?.message || 'Failed to delete source');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to delete source');
    },
  });

  // Validate URL format
  const validateUrl = (url: string): boolean => {
    const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    return urlPattern.test(url);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: { url?: string; name?: string } = {};

    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else if (!validateUrl(formData.url)) {
      errors.url = 'Please enter a valid URL (must start with http:// or https://)';
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = (source?: ContentSource) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        url: source.url,
        name: source.name,
        is_active: source.is_active,
        crawl_depth: source.scraping_config?.crawl_depth ?? 0,
        max_pages: source.scraping_config?.max_pages ?? 10,
      });
    } else {
      setEditingSource(null);
      setFormData({
        url: '',
        name: '',
        is_active: true,
        crawl_depth: 0,
        max_pages: 10,
      });
    }
    setFormErrors({});
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSource(null);
    setFormData({
      url: '',
      name: '',
      is_active: true,
      crawl_depth: 0,
      max_pages: 10,
    });
    setFormErrors({});
    setError(null);
  };

  const handleOpenDeleteDialog = (source: ContentSource) => {
    setDeletingSource(source);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingSource(null);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (editingSource) {
      updateMutation.mutate({ id: editingSource.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = () => {
    if (deletingSource) {
      deleteMutation.mutate(deletingSource.id);
    }
  };

  const sources: ContentSource[] = sourcesData?.data?.sources || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Content Sources</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Source
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Crawl</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : sources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No sources found. Add your first source to get started.
                </TableCell>
              </TableRow>
            ) : (
              sources.map((source) => (
                <TableRow key={source.id}>
                  <TableCell>{source.name}</TableCell>
                  <TableCell>
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.url}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={source.is_active ? 'Active' : 'Inactive'}
                      color={source.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {(source.scraping_config?.crawl_depth ?? 0) > 0 ? (
                      <Chip
                        label={`D${source.scraping_config.crawl_depth} / ${source.scraping_config.max_pages ?? 10}p`}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Single" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(source.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(source)}
                      title="Edit source"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(source)}
                      title="Delete source"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSource ? 'Edit Source' : 'Add New Source'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
              required
            />
            <TextField
              label="URL"
              value={formData.url}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, url: e.target.value })}
              error={!!formErrors.url}
              helperText={formErrors.url || 'Must start with http:// or https://'}
              fullWidth
              required
              placeholder="https://example.com"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <TextField
              label="Crawl Depth"
              type="number"
              value={formData.crawl_depth}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, crawl_depth: Math.max(0, Math.min(5, parseInt(e.target.value) || 0)) })}
              fullWidth
              inputProps={{ min: 0, max: 5 }}
              helperText="0 = single page, 1-5 = deep crawl depth"
            />
            <TextField
              label="Max Pages"
              type="number"
              value={formData.max_pages}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, max_pages: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) })}
              fullWidth
              inputProps={{ min: 1, max: 100 }}
              helperText="Maximum number of pages to crawl (1-100)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingSource ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the source "{deletingSource?.name}"?
            This will deactivate the source, but historical articles will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SourceManager;
